// Dashboard Page
import { useEffect, useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, FolderOpen, Globe, Clock, CheckCircle, XCircle, Edit, Download, Zap, Layers } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAgentStore } from '../stores/agentStore';
import { useWorkflowsStore } from '../stores/workflowsStore';
import { useAuthStore } from '../stores/authStore';
import { useUIStore } from '../stores/uiStore';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { OnboardingModal } from '../components/onboarding/OnboardingModal';

// PERF-001: Utilidad pura extraída fuera del componente para evitar recreación
// Nota: updatedAt puede ser Firestore Timestamp, Date, o número según el origen de datos
const getTime = (ts: unknown): number => {
  if (!ts) return 0;
  if (typeof ts === 'number') return ts;
  if (ts instanceof Date) return ts.getTime();
  if (typeof (ts as { toMillis?: () => number }).toMillis === 'function') return (ts as { toMillis: () => number }).toMillis();
  if (typeof (ts as { seconds?: number }).seconds === 'number') return (ts as { seconds: number }).seconds * 1000;
  return 0;
};

// Formato relativo de tiempo: "Justo ahora", "Hace 5m", "Hace 3h", etc.
const formatRelativeTime = (timestamp: number): string => {
  if (!timestamp) return '';
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (minutes < 1) return 'Justo ahora';
  if (minutes < 60) return `Hace ${minutes}m`;
  if (hours < 24) return `Hace ${hours}h`;
  if (days < 7) return `Hace ${days}d`;
  if (days < 30) return `Hace ${Math.floor(days / 7)} sem`;
  return new Date(timestamp).toLocaleDateString();
};

// Gradientes para avatares de workflows (rota cíclicamente por índice)
const avatarGradients = [
  'from-emerald-500 to-teal-500',
  'from-teal-500 to-cyan-500',
  'from-cyan-500 to-emerald-500',
  'from-emerald-600 to-cyan-500',
  'from-teal-600 to-emerald-400',
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { status } = useAgentStore();
  // H-F033: Incluir error del store para mostrarlo si fetchWorkflows falla
  const { workflows, loading, error: workflowsError, fetchWorkflows, clearError } = useWorkflowsStore();
  const { user } = useAuthStore();
  const { onboardingDismissed, dismissOnboarding } = useUIStore();
  const [showOnboarding, setShowOnboarding] = useState(false);

  // PLAT-001: Actualizar título de la pestaña para screen readers y navegación
  useDocumentTitle('Dashboard');

  // Mostrar onboarding con delay para permitir hidratación del store
  useEffect(() => {
    if (user && !onboardingDismissed) {
      const timer = setTimeout(() => setShowOnboarding(true), 300);
      return () => clearTimeout(timer);
    }
  }, [user, onboardingDismissed]);

  // NAV-06: Cargar workflows al montar el Dashboard para datos actualizados.
  // storeSubscriptions carga al login, pero si el usuario navega Dashboard→otra página→Dashboard,
  // los datos podrían estar desactualizados (ej: workflow editado desde otra pestaña).
  useEffect(() => {
    if (user) {
      fetchWorkflows(user.uid);
    }
  }, [user, fetchWorkflows]);

  const agentBadge = {
    connected: { variant: 'success' as const, className: '', label: 'Agente Conectado' },
    degraded: {
      variant: 'secondary' as const,
      className: 'bg-amber-100 text-amber-900 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-700',
      label: 'Agente Degradado',
    },
    unauthorized: { variant: 'destructive' as const, className: '', label: 'API Key Inválida' },
    timeout: {
      variant: 'secondary' as const,
      className: 'bg-amber-100 text-amber-900 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-700',
      label: 'Agente con Timeout',
    },
    error: { variant: 'destructive' as const, className: '', label: 'Error del Agente' },
    disconnected: { variant: 'destructive' as const, className: '', label: 'Agente Desconectado' },
  }[status.status];

  // Calcular estadísticas
  const totalWorkflows = workflows.length;
  const totalExecutions = workflows.reduce((sum, w) => sum + w.executionCount, 0);
  const totalSuccess = workflows.reduce((sum, w) => sum + w.successCount, 0);
  const totalFailures = workflows.reduce((sum, w) => sum + w.failureCount, 0);
  const hasStats = totalExecutions > 0;

  // PERF-001: Memoizar y copiar con spread antes de sort para no mutar el array del store
  const recentWorkflows = useMemo(() =>
    [...workflows]
      .sort((a, b) => {
        const aTime = getTime(a.lastExecutedAt) || getTime(a.updatedAt);
        const bTime = getTime(b.lastExecutedAt) || getTime(b.updatedAt);
        return bTime - aTime;
      })
      .slice(0, 5),
    [workflows]
  );

  const stats = [
    {
      label: 'Workflows Totales', value: totalWorkflows.toString(), icon: FolderOpen,
      iconBg: 'bg-emerald-100 dark:bg-emerald-900/30', iconColor: 'text-emerald-600 dark:text-emerald-400',
    },
    {
      label: 'Ejecuciones Totales', value: totalExecutions.toString(), icon: Clock,
      iconBg: 'bg-teal-100 dark:bg-teal-900/30', iconColor: 'text-teal-600 dark:text-teal-400',
    },
    {
      label: 'Exitosas', value: totalSuccess.toString(), icon: CheckCircle,
      iconBg: 'bg-green-100 dark:bg-green-900/30', iconColor: 'text-green-600 dark:text-green-400',
    },
    {
      label: 'Fallidas', value: totalFailures.toString(), icon: XCircle,
      iconBg: 'bg-red-100 dark:bg-red-900/30', iconColor: 'text-red-600 dark:text-red-400',
    },
  ];

  const userName = user?.email?.split('@')[0] || 'Usuario';

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8 dot-pattern min-h-full">
      {/* Modal de onboarding */}
      <OnboardingModal
        open={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onDismiss={dismissOnboarding}
      />

      {/* Header — banner de bienvenida con gradiente sutil */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 dark:from-emerald-800 dark:via-teal-800 dark:to-cyan-800 p-6 sm:p-8 text-white animate-fade-in-up">
        {/* Patrón decorativo de fondo */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/4" />
        </div>
        <div className="relative flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">
              Hola, {userName}
            </h1>
            <p className="text-emerald-100 dark:text-emerald-200 mt-1">Panel de control de ProcesoFácil</p>
          </div>
          <Badge variant={agentBadge.variant} className={agentBadge.className}>
            {agentBadge.label}
          </Badge>
        </div>
      </div>

      {/* H-F033: Banner de error si fetchWorkflows falla */}
      {/* HE-001: Botones de reintento y descartar para recuperación de errores */}
      {workflowsError && (
        <div role="alert" className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center justify-between gap-4">
          <span className="text-red-700 dark:text-red-400 text-sm">
            Error al cargar los workflows: {workflowsError}
          </span>
          <div className="flex gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={() => user && fetchWorkflows(user.uid)}>
              Reintentar
            </Button>
            <Button variant="ghost" size="sm" onClick={clearError}>
              Descartar
            </Button>
          </div>
        </div>
      )}

      {/* Onboarding para usuarios nuevos */}
      {!loading && totalWorkflows === 0 && !workflowsError && (
        <Card className="border-emerald-200 dark:border-emerald-800 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30">
          <CardHeader>
            <CardTitle>Primeros Pasos</CardTitle>
            <CardDescription>Configura ProcesoFácil en 3 sencillos pasos</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* A11Y-002: Convertido a Link para accesibilidad de teclado y screen reader */}
              <Link
                to="/download"
                className="flex flex-col items-center text-center p-4 rounded-lg bg-white/60 dark:bg-gray-800/60 hover:bg-white/80 dark:hover:bg-gray-800/80 hover:shadow-md transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
              >
                <div className="rounded-full bg-emerald-100 dark:bg-emerald-900/30 p-4 mb-3">
                  <Download className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">1. Instala el Agente</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Descarga e instala el agente en tu PC con Windows</p>
              </Link>
              {/* DS-002: Unificado green → emerald para consistencia */}
              <div className="flex flex-col items-center text-center p-4 rounded-lg bg-white/60 dark:bg-gray-800/60">
                <div className="rounded-full bg-emerald-100 dark:bg-emerald-900/30 p-4 mb-3">
                  <Plus className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">2. Crea un Workflow</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Diseña tu primer flujo de automatización</p>
              </div>
              <div className="flex flex-col items-center text-center p-4 rounded-lg bg-white/60 dark:bg-gray-800/60">
                <div className="rounded-full bg-purple-100 dark:bg-purple-900/30 p-4 mb-3">
                  <Zap className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">3. Ejecútalo</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Observa cómo se automatiza tu tarea</p>
              </div>
            </div>
            <div className="flex justify-center mt-6">
              <Button onClick={() => navigate('/workflows/new')}>
                <Plus className="h-4 w-4 mr-2" />
                Crear mi Primer Workflow
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid - solo cuando hay datos útiles */}
      {/* AESTHETIC-002: Animación de entrada staggered */}
      {hasStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 animate-fade-in-up">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card
                key={stat.label}
                className="hover:shadow-md transition-shadow"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <CardHeader className="flex flex-row items-center gap-4 space-y-0 pb-2">
                  <div className={`rounded-lg p-2.5 ${stat.iconBg}`}>
                    <Icon className={`h-5 w-5 ${stat.iconColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      {stat.label}
                    </CardTitle>
                    <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-0.5">{stat.value}</div>
                  </div>
                </CardHeader>
              </Card>
            );
          })}
        </div>
      )}

      {/* Quick Actions */}
      {/* A11Y-001: Convertido cards clickeables a Links para accesibilidad */}
      {/* AESTHETIC-002: Animación de entrada staggered */}
      <div className="animate-fade-in-up" style={{ animationDelay: '100ms' }}>
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">Acciones Rápidas</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          <Link to="/workflows/new" className="group block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 rounded-lg">
            <Card className="hover-lift h-full border-transparent hover:border-emerald-200 dark:hover:border-emerald-800">
              <CardHeader>
                <div className="flex items-center gap-4">
                  <div className="rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 p-3 shadow-lg shadow-emerald-500/20 group-hover:shadow-emerald-500/40 transition-shadow">
                    <Plus className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Nuevo Workflow</CardTitle>
                    <CardDescription>Crear un nuevo flujo de automatización</CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>
          </Link>

          <Link to="/workflows" className="group block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 rounded-lg">
            <Card className="hover-lift h-full border-transparent hover:border-emerald-200 dark:hover:border-emerald-800">
              <CardHeader>
                <div className="flex items-center gap-4">
                  <div className="rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 p-3 shadow-lg shadow-teal-500/20 group-hover:shadow-teal-500/40 transition-shadow">
                    <FolderOpen className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Mis Workflows</CardTitle>
                    <CardDescription>Ver y gestionar tus workflows</CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>
          </Link>

          {/* HE-002: cursor-not-allowed, title y aria-disabled para card deshabilitada */}
          <Card className="opacity-50 cursor-not-allowed" title="Función disponible próximamente" aria-disabled="true">
            <CardHeader>
              <div className="flex items-center gap-4">
                <div className="rounded-xl bg-gray-200 dark:bg-gray-700 p-3">
                  <Globe className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                </div>
                <div>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    Galería
                    <Badge variant="secondary" className="text-xs">Próximamente</Badge>
                  </CardTitle>
                  <CardDescription>Explorar workflows de la comunidad</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>
        </div>
      </div>

      {/* Recent Workflows */}
      {/* AESTHETIC-002: Animación de entrada staggered */}
      {totalWorkflows > 0 && (
        <Card className="animate-fade-in-up" style={{ animationDelay: '200ms' }}>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Workflows Recientes</CardTitle>
              <CardDescription>Últimos workflows ejecutados o modificados</CardDescription>
            </div>
            {/* HE-003: Enlace "Ver todos" para acceder a la lista completa */}
            <Link
              to="/workflows"
              className="text-sm text-emerald-600 dark:text-emerald-400 hover:underline underline-offset-4 flex-shrink-0"
            >
              Ver todos
            </Link>
          </CardHeader>
          <CardContent>
            {loading ? (
              /* A11Y-003: aria-busy y aria-label para screen readers */
              <div aria-busy="true" aria-label="Cargando workflows recientes" className="space-y-3 animate-pulse">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex items-center gap-4 p-4 rounded-lg">
                    <div className="h-10 w-10 bg-gray-200 dark:bg-gray-700 rounded-full flex-shrink-0" />
                    <div className="space-y-2 flex-1">
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                    </div>
                    <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {recentWorkflows.map((workflow, index) => {
                  const hasExecutions = workflow.executionCount > 0;
                  const successRate = hasExecutions ? workflow.successCount / workflow.executionCount : 0;
                  const gradient = avatarGradients[index % avatarGradients.length];
                  const lastTime = getTime(workflow.lastExecutedAt) || getTime(workflow.updatedAt);
                  return (
                    /* A11Y-001: Convertido div clickeable a Link para accesibilidad */
                    <Link
                      key={workflow.id}
                      to={`/workflows/${workflow.id}`}
                      className="group flex items-center gap-4 p-4 -mx-2 rounded-lg hover:bg-gradient-to-r hover:from-gray-50 hover:to-emerald-50/40 dark:hover:from-gray-800/60 dark:hover:to-emerald-900/20 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
                    >
                      {/* Avatar circular con inicial del workflow */}
                      <div className={`flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center text-white font-bold text-sm shadow-sm group-hover:shadow-md transition-shadow`}>
                        {workflow.name.charAt(0).toUpperCase()}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate group-hover:text-emerald-700 dark:group-hover:text-emerald-400 transition-colors">{workflow.name}</h3>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-gray-500 dark:text-gray-400">
                          <span className="inline-flex items-center gap-1">
                            <Layers className="h-3.5 w-3.5" />
                            {workflow.nodes.length} pasos
                          </span>
                          {lastTime > 0 && (
                            <span>{formatRelativeTime(lastTime)}</span>
                          )}
                        </div>
                        {/* Barra de progreso de éxito */}
                        {hasExecutions && (
                          <div className="flex items-center gap-2 mt-2">
                            <div className="h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden w-24">
                              <div
                                className={`h-full rounded-full transition-all ${successRate >= 0.5 ? 'bg-gradient-to-r from-emerald-500 to-teal-400' : 'bg-gradient-to-r from-red-400 to-red-500'}`}
                                style={{ width: `${Math.max(successRate * 100, 4)}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">
                              {workflow.successCount}/{workflow.executionCount}
                            </span>
                          </div>
                        )}
                      </div>

                      <Button
                        variant="ghost"
                        size="icon"
                        className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                        onClick={(e) => { e.stopPropagation(); navigate(`/workflows/${workflow.id}`); }}
                        title="Editar"
                        aria-label={`Editar ${workflow.name}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
